/**
 * @description
 * Custom hook for managing ClientDetail page data and state.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { useState, useEffect, useCallback } from 'react';
import { useClient } from '../../../hooks/useClients';
import { useDeployments } from '../../../hooks/useDeployments';
import { useDeploymentReadiness } from '../../../hooks/useDeploymentReadiness';
import { useAuth } from '../../../hooks/useAuth';
import { api } from '../../../services/api';
import { toast } from 'react-hot-toast';
import type { ClientDetailState } from '../types';

export function useClientData(clientId: string) {
  const { client, loading } = useClient(clientId);
  const { deployments, loading: deploymentsLoading, createDeployment, refetch: refetchDeployments } = useDeployments(clientId);
  const { user } = useAuth();
  const { readiness, loading: readinessLoading } = useDeploymentReadiness(clientId);

  const [state, setState] = useState<ClientDetailState>({
    activeTab: 'overview',
    configs: {},
    configsLoading: false,
    editingKey: null,
    editValue: '',
    showSecrets: {},
    saving: false,
    deploying: false,
    provisioningDatabase: false,
    deploymentStatus: null,
    loadingDeploymentStatus: false,
  });

  const latestDeployment = deployments[0];

  const loadConfigs = useCallback(async () => {
    if (!clientId) return;
    setState(prev => ({ ...prev, configsLoading: true }));
    try {
      const data = await api.getClientConfigs(clientId);
      const configMap: Record<string, string> = {};
      data.forEach((config: { key: string; value: string }) => {
        configMap[config.key] = config.value;
      });
      setState(prev => ({ ...prev, configs: configMap }));
    } catch (error) {
      toast.error('Failed to load configurations');
      console.error('Error loading configs:', error);
    } finally {
      setState(prev => ({ ...prev, configsLoading: false }));
    }
  }, [clientId]);

  // Load configurations when config tab is active
  useEffect(() => {
    if (state.activeTab === 'config' && clientId) {
      loadConfigs();
    }
  }, [state.activeTab, clientId, loadConfigs]);

  const handleInitializeDefaults = async () => {
    if (!clientId) return;
    setState(prev => ({ ...prev, saving: true }));
    try {
      await api.initializeClientConfigs(clientId);
      await loadConfigs();
      toast.success('Default configurations initialized');
    } catch (error) {
      toast.error('Failed to initialize defaults');
      console.error('Error initializing defaults:', error);
    } finally {
      setState(prev => ({ ...prev, saving: false }));
    }
  };

  const handleSaveConfig = async (key: string) => {
    if (!clientId) return;
    setState(prev => ({ ...prev, saving: true }));
    try {
      await api.updateClientConfig(clientId, key, state.editValue);
      setState(prev => ({ 
        ...prev, 
        configs: { ...prev.configs, [key]: prev.editValue },
        editingKey: null,
        editValue: ''
      }));
      toast.success('Configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
      console.error('Error saving config:', error);
    } finally {
      setState(prev => ({ ...prev, saving: false }));
    }
  };

  const handleDeleteConfig = async (key: string) => {
    if (!clientId || !confirm(`Delete configuration ${key}?`)) return;
    setState(prev => ({ ...prev, saving: true }));
    try {
      await api.deleteClientConfig(clientId, key);
      const newConfigs = { ...state.configs };
      delete newConfigs[key];
      setState(prev => ({ ...prev, configs: newConfigs }));
      toast.success('Configuration deleted');
    } catch (error) {
      toast.error('Failed to delete configuration');
      console.error('Error deleting config:', error);
    } finally {
      setState(prev => ({ ...prev, saving: false }));
    }
  };

  const startEdit = (key: string, currentValue: string) => {
    setState(prev => ({
      ...prev,
      editingKey: key,
      editValue: currentValue || ''
    }));
  };

  const cancelEdit = () => {
    setState(prev => ({
      ...prev,
      editingKey: null,
      editValue: ''
    }));
  };

  const toggleSecretVisibility = (key: string) => {
    setState(prev => ({
      ...prev,
      showSecrets: { ...prev.showSecrets, [key]: !prev.showSecrets[key] }
    }));
  };

  const handleDeploy = async () => {
    if (!clientId || !user || !readiness) return;

    if (!readiness.isReady) {
      toast.error('Client is not ready for deployment. Please configure required settings first.');
      return;
    }

    try {
      setState(prev => ({ ...prev, deploying: true }));
      await createDeployment(clientId, user.id);
      toast.success('Deployment started successfully!');
      refetchDeployments();
    } catch (error) {
      console.error('Deployment error:', error);
      toast.error('Failed to start deployment');
    } finally {
      setState(prev => ({ ...prev, deploying: false }));
    }
  };

  const handleProvisionDatabase = async () => {
    if (!clientId || !client) return;

    if (!confirm(
      'This will create a D1 database for this client. ' +
      'The database will be initialized with the required schema. Continue?'
    )) {
      return;
    }

    try {
      setState(prev => ({ ...prev, provisioningDatabase: true }));
      await api.provisionDatabase(clientId);
      toast.success('Database provisioned successfully', { duration: 5000 });
      window.location.reload();
    } catch (error) {
      console.error('Error provisioning database:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Failed to provision database: ${errorMessage}`, { duration: 7000 });
    } finally {
      setState(prev => ({ ...prev, provisioningDatabase: false }));
    }
  };

  const fetchDeploymentStatus = useCallback(async () => {
    if (!clientId) return;
    setState(prev => ({ ...prev, loadingDeploymentStatus: true }));
    try {
      const status = await api.getClientDeploymentStatus(clientId);
      setState(prev => ({ ...prev, deploymentStatus: status.status?.processStatus || null }));
    } catch (error) {
      console.error('Error fetching deployment status:', error);
    } finally {
      setState(prev => ({ ...prev, loadingDeploymentStatus: false }));
    }
  }, [clientId]);

  useEffect(() => {
    if (state.activeTab === 'overview' && clientId) {
      fetchDeploymentStatus();
    }
  }, [state.activeTab, clientId, fetchDeploymentStatus]);

  const handleStartLocalDeployment = async () => {
    if (!clientId || !client) return;
    
    if (!client.databaseId) {
      toast.error('Please provision a database first');
      return;
    }

    try {
      setState(prev => ({ ...prev, deploying: true }));
      await api.startDeployment(clientId, 'local');
      toast.success('Local deployment started successfully');
      await fetchDeploymentStatus();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start deployment');
    } finally {
      setState(prev => ({ ...prev, deploying: false }));
    }
  };

  const handleStopDeployment = async () => {
    if (!clientId) return;
    
    try {
      await api.stopDeployment(clientId);
      toast.success('Deployment stopped');
      await fetchDeploymentStatus();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to stop deployment');
    }
  };

  const setActiveTab = (tab: ClientDetailState['activeTab']) => {
    setState(prev => ({ ...prev, activeTab: tab }));
  };

  return {
    // Data
    client,
    loading,
    deployments,
    deploymentsLoading,
    latestDeployment,
    readiness,
    readinessLoading,
    user,
    
    // State
    ...state,
    
    // Actions
    setActiveTab,
    loadConfigs,
    handleInitializeDefaults,
    handleSaveConfig,
    handleDeleteConfig,
    startEdit,
    cancelEdit,
    toggleSecretVisibility,
    handleDeploy,
    handleProvisionDatabase,
    handleStartLocalDeployment,
    handleStopDeployment,
    fetchDeploymentStatus,
    refetchDeployments,
  };
}
